/**
 * Phase 1.7 — Animals module index, v2.
 *
 * Cards now show the most-recent uploaded image via EntityImage instead
 * of always showing the brand-mark fallback.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bug, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Card,
  EmptyState,
  FilterBar,
  KpiCard,
  SectionHeader,
  ViewToggle,
} from '@/components/v2';
import type { ViewMode } from '@/components/v2';
import { EntityImage } from '@/components/EntityImage';
import { authFetch } from '@/lib/auth';
import { useUiPrefs } from '@/lib/layout-flag';
import { usePageHeader } from '@/components/v2/layout/Header';
import { AddAnimalDialog } from '@/components/v2/dialogs/AddAnimalDialog';
import type { Species } from '@shared/types';

interface AnimalRow {
  id: string;
  name: string;
  species_id: string | null;
  breed: string | null;
  tag_number: string | null;
  dob: string | null;
  status: string | null;
  pasture_id: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

const UNSPECIFIED = '__unspecified__' as const;
type SpeciesGroupKey = string | typeof UNSPECIFIED;

const STATUS_TONE: Record<string, string> = {
  active: 'bg-moss/10 text-moss dark:bg-moss-dark/20 dark:text-moss-dark',
  sold: 'bg-clay/10 text-clay dark:bg-clay-dark/20 dark:text-clay-dark',
  deceased: 'bg-charcoal/10 text-charcoal dark:bg-charcoal-dark/20 dark:text-charcoal-dark',
  retired: 'bg-charcoal/10 text-charcoal dark:bg-charcoal-dark/20 dark:text-charcoal-dark',
  quarantined: 'bg-clay/10 text-clay dark:bg-clay-dark/20 dark:text-clay-dark',
};

const VIEW_MODE_KEY = 'animals';

export function AnimalsPageV2(): JSX.Element {
  const navigate = useNavigate();
  const { prefs, patch } = useUiPrefs();
  const view: ViewMode = prefs.view_modes?.[VIEW_MODE_KEY] ?? 'grid';
  const [animals, setAnimals] = useState<AnimalRow[]>([]);
  const [species, setSpecies] = useState<Species[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [speciesFilter, setSpeciesFilter] = useState<'all' | SpeciesGroupKey>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [addOpen, setAddOpen] = useState<boolean>(false);
  const [refreshTick, setRefreshTick] = useState<number>(0);

  usePageHeader(
    'Animals',
    <Button
      size="sm"
      onClick={() => setAddOpen(true)}
      className="bg-burnt-orange text-white hover:bg-burnt-orange/90 dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90"
    >
      <Plus className="h-4 w-4" />
      Add animal
    </Button>,
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    async function load(): Promise<void> {
      try {
        const [animalsRes, speciesRes] = await Promise.all([
          authFetch<{ items: AnimalRow[] }>('/api/animals'),
          authFetch<{ items: Species[] }>('/api/species'),
        ]);
        if (!cancelled) {
          setAnimals(animalsRes.items ?? []);
          setSpecies(speciesRes.items ?? []);
        }
      } catch (err) {
        if (!cancelled) setError((err as Error).message ?? 'Failed to load animals');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => { cancelled = true; };
  }, [refreshTick]);

  const speciesById = useMemo(() => {
    const m = new Map<string, Species>();
    for (const s of species) m.set(s.id, s);
    return m;
  }, [species]);

  const groupOrder = useMemo<SpeciesGroupKey[]>(() => {
    return [...species.map((s) => s.id), UNSPECIFIED];
  }, [species]);

  const labelFor = (key: SpeciesGroupKey): string => {
    if (key === UNSPECIFIED) return 'Unspecified';
    return speciesById.get(key)?.name ?? 'Unknown';
  };

  const knownStatuses = useMemo<string[]>(() => {
    const s = new Set<string>();
    for (const a of animals) if (a.status) s.add(a.status);
    return [...s].sort();
  }, [animals]);

  const filtered = useMemo<AnimalRow[]>(() => {
    const q = search.trim().toLowerCase();
    return animals.filter((a) => {
      if (q) {
        const haystack = `${a.name ?? ''} ${a.tag_number ?? ''} ${a.breed ?? ''}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (speciesFilter !== 'all') {
        const key: SpeciesGroupKey = a.species_id ?? UNSPECIFIED;
        if (key !== speciesFilter) return false;
      }
      if (statusFilter !== 'all' && (a.status ?? '') !== statusFilter) return false;
      return true;
    });
  }, [animals, search, speciesFilter, statusFilter]);

  const grouped = useMemo<Record<string, AnimalRow[]>>(() => {
    const out: Record<string, AnimalRow[]> = {};
    for (const key of groupOrder) out[key] = [];
    for (const a of filtered) {
      const key: SpeciesGroupKey = a.species_id ?? UNSPECIFIED;
      const bucket = key === UNSPECIFIED || speciesById.has(key) ? key : UNSPECIFIED;
      (out[bucket] ??= []).push(a);
    }
    return out;
  }, [filtered, groupOrder, speciesById]);

  const totals = useMemo(() => {
    const active = animals.filter((a) => (a.status ?? '').toLowerCase() === 'active').length;
    const closed = animals.length - active;
    return { active, closed };
  }, [animals]);

  const setView = (next: ViewMode): void => {
    void patch({ view_modes: { ...(prefs.view_modes ?? {}), [VIEW_MODE_KEY]: next } });
  };

  const hasActiveFilters = Boolean(search) || speciesFilter !== 'all' || statusFilter !== 'all';
  const clear = (): void => { setSearch(''); setSpeciesFilter('all'); setStatusFilter('all'); };

  const distinctSpeciesCount = groupOrder.filter((s) => (grouped[s]?.length ?? 0) > 0).length;

  return (
    <div className="space-y-6">
      <AddAnimalDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        trigger={<span className="hidden" />}
        onAdded={() => setRefreshTick((t) => t + 1)}
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <KpiCard
          label="Animals"
          icon={Bug}
          primary={{ label: 'Active', value: totals.active }}
          secondary={{ label: 'Sold/Deceased', value: totals.closed, tone: 'warning' }}
        />
        <KpiCard
          label="Filter"
          primary={{ label: 'Showing', value: filtered.length }}
          secondary={{ label: 'Total', value: animals.length }}
        />
        <KpiCard
          label="Species"
          primary={{ label: 'Distinct', value: distinctSpeciesCount }}
        />
      </div>

      <FilterBar
        search={{ value: search, onChange: setSearch, placeholder: 'Search animals or tags' }}
        filters={[
          <FilterSelect
            key="species"
            value={speciesFilter}
            onChange={(v) => setSpeciesFilter(v as 'all' | SpeciesGroupKey)}
            options={[
              { value: 'all', label: 'All species' },
              ...species.map((s) => ({ value: s.id, label: s.name })),
              { value: UNSPECIFIED, label: 'Unspecified' },
            ]}
          />,
          <FilterSelect
            key="status"
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { value: 'all', label: 'All statuses' },
              ...knownStatuses.map((s) => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) })),
            ]}
          />,
        ]}
        actions={<ViewToggle value={view} onChange={setView} />}
        onClear={clear}
        hasActiveFilters={hasActiveFilters}
      />

      {error ? (
        <div className="rounded-lg bg-clay/10 p-4 text-sm text-clay dark:bg-clay-dark/20 dark:text-clay-dark">
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-64 rounded-xl" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Bug}
          title={hasActiveFilters ? 'No matches' : 'No animals yet'}
          description={
            hasActiveFilters
              ? 'Try clearing filters to see the full herd.'
              : 'Add your first animal to start tracking weights, health events, and pasture rotations.'
          }
          action={
            hasActiveFilters ? (
              <Button onClick={clear} variant="outline">Clear filters</Button>
            ) : (
              <Button onClick={() => setAddOpen(true)}>
                <Plus className="h-4 w-4" />
                Add animal
              </Button>
            )
          }
        />
      ) : (
        groupOrder
          .filter((key) => (grouped[key]?.length ?? 0) > 0)
          .map((key) => (
            <SectionHeader
              key={key}
              title={labelFor(key)}
              count={grouped[key]?.length ?? 0}
              collapsible
            >
              {view === 'grid' ? (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {(grouped[key] ?? []).map((a) => (
                    <AnimalCard
                      key={a.id}
                      animal={a}
                      speciesName={key === UNSPECIFIED ? null : labelFor(key)}
                      onOpen={() => navigate(`/animals/${a.id}`)}
                    />
                  ))}
                </div>
              ) : (
                <div className="overflow-hidden rounded-xl border border-border bg-surface dark:bg-surface-dark dark:border-border">
                  <table className="w-full text-sm">
                    <thead className="border-b border-border bg-surface-raised text-left dark:bg-surface-raised-dark">
                      <tr>
                        <Th>Name</Th>
                        <Th>Tag</Th>
                        <Th>Breed</Th>
                        <Th>Status</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {(grouped[key] ?? []).map((a) => (
                        <tr
                          key={a.id}
                          className="cursor-pointer border-b border-border last:border-0 hover:bg-surface-raised dark:hover:bg-surface-raised-dark"
                          onClick={() => navigate(`/animals/${a.id}`)}
                        >
                          <Td className="font-medium">{a.name}</Td>
                          <Td className="font-mono text-xs">{a.tag_number || '–'}</Td>
                          <Td className="text-muted-foreground">{a.breed || '–'}</Td>
                          <Td>
                            <StatusPill status={a.status} />
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </SectionHeader>
          ))
      )}
    </div>
  );
}

function AnimalCard({
  animal,
  speciesName,
  onOpen,
}: {
  animal: AnimalRow;
  speciesName: string | null;
  onOpen: () => void;
}): JSX.Element {
  const subtitleBits: string[] = [];
  if (animal.tag_number) subtitleBits.push(`Tag ${animal.tag_number}`);
  if (animal.breed) subtitleBits.push(animal.breed);
  return (
    <Card
      interactive
      onClick={onOpen}
      thumbnail={<EntityImage entityType="animal" entityId={animal.id} alt={animal.name} />}
      title={animal.name}
      subtitle={subtitleBits.join(' · ') || (speciesName ?? 'Animal')}
      badge={<StatusPill status={animal.status} />}
      footer={
        <div className="flex items-center justify-between">
          <span className="text-burnt-orange dark:text-burnt-orange-dark">Open</span>
          <Trash2 className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        </div>
      }
    />
  );
}

function StatusPill({ status }: { status: string | null }): JSX.Element | null {
  if (!status) return null;
  const tone = STATUS_TONE[status.toLowerCase()] ?? 'bg-surface-raised text-muted-foreground dark:bg-surface-raised-dark';
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 font-mono text-2xs uppercase tracking-wider ${tone}`}>
      {status}
    </span>
  );
}

function FilterSelect({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}): JSX.Element {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 rounded-md border border-border bg-surface px-2 text-sm shadow-sm dark:bg-surface-dark dark:border-border"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function Th({ children }: { children: React.ReactNode }): JSX.Element {
  return <th className="px-4 py-2 font-mono text-2xs uppercase tracking-wider text-muted-foreground">{children}</th>;
}

function Td({ children, className }: { children: React.ReactNode; className?: string }): JSX.Element {
  return <td className={`px-4 py-3 ${className ?? ''}`}>{children}</td>;
}
